import * as _ from 'lodash'
import { Variable } from 'alkali'
import type { DAStudyGroup, DAStudyRow, EGroup, ERow, ExtractionUpdate, REFullStudy } from 'services/extract/types'
import { indexCells, compareGroup, compareRow, evalGroup, evalRow, studyIsAccessible } from 'components/extract/model/comparison'
import {
	DAOutcome, DASearchCharacteristic,
	DASearchIntervention,
	DASearchOutcome,
	getStudyRow,
	QueryAnnotations
} from 'components/extract/model/extraction'

/**
 * Aggregates top-level study information and flags
 */
export default class StudyContext {
	readonly clientId: number
	readonly isAdmin: boolean
	readonly isReAdmin: boolean
	readonly isSuper: boolean
	readonly isPrivate: boolean = this.study?.metadata?.Private
	readonly groups: DAStudyGroup[] = this.study?.groups || []
	readonly studyLevel: DAStudyRow[] = (this.study?.studyLevel || [])
	readonly groupLevel: DAStudyRow[] = (this.study?.groupLevel || [])
	readonly characteristics: DAStudyRow[] = (this.study?.characteristics || [])
	readonly outcomes: DAStudyRow[] = (this.study?.outcomes || []).map(o => getStudyRow(o, this.allOutcomes))
	readonly groupsById = _.keyBy(this.groups, g => g.Id)
	readonly allRows = [...this.studyLevel, ...this.groupLevel, ...this.characteristics, ...this.outcomes]
	readonly rowsById = _.keyBy(this.allRows, r => r.id)
	readonly indexedCells = indexCells(this.groups, this.allRows)
	readonly extractions: ExtractionUpdate[]
	readonly isAccessible: boolean
	readonly canCreateGroups: boolean
	constructor(readonly study: REFullStudy, readonly user,
	            readonly searchedConditions: DASearchCharacteristic[] = [], readonly searchedOutcomes: DASearchOutcome[] = [], readonly searchedItvns: DASearchIntervention[] = [],
	            readonly queryAnnotations: Variable<QueryAnnotations> = new Variable(),
	            readonly allOutcomes?: DAOutcome[]) {
		this.clientId = user.Client?.Id
		this.isAdmin = !!user.FeatureConfig?.['Bindings Admin']?.Enabled
		this.isReAdmin = !!user.FeatureConfig?.['Rapid Extract Admin']?.Enabled

		//EV-1755 give RE Admin same permissions as SuperAdmin
		this.isSuper = !!user.Roles?.find(r => r.Name == 'dre_super_admin') || this.isReAdmin
		this.extractions = study?.extractions || []
		this.isAccessible = studyIsAccessible(study, user)
		this.canCreateGroups = !this.study?.hasDocData || this.user?.FeatureConfig?.['Rapid Extract Admin']?.Enabled
	}

	compareGroup(group: EGroup) {
		return compareGroup(group, this.groups, this.clientId, this.isAccessible)
	}

	compareRow(row: ERow) {
		return compareRow(row, this.outcomes, this.clientId, this.isAccessible)
	}

	evalGroup(id: string) {
		return this.groupsById[id] && evalGroup(this.groupsById[id], this.clientId, this.isAccessible)
	}

	evalRow(section: string, id: string) {
		return this.rowsById[id] && evalRow(section, this.rowsById[id], this.clientId, this.isAccessible)
	}
}